import { useQuery } from '@tanstack/react-query';
import {
  Percent,
  X,
  Loader2,
  AlertCircle,
  RefreshCcw,
  Users,
  ClipboardList,
  TrendingDown,
  TrendingUp,
  ShieldCheck,
  Info,
  CheckCircle,
} from 'lucide-react';
import {
  memo,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  useState,
  JSX,
} from 'react';

// Lazy load the table component for better code splitting
const CompetitorsDiscountTable = lazy(() => import('./Table'));

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { GetItemOffersToday } from '@/pages/PurchasesContainer/PurchasesRequests/api/PurchasesRequests.api';
import type {
  IOneItemInPrepareForRequest,
  ItemOfferToday,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  item: IOneItemInPrepareForRequest;
};

// Stats card component for reusability
interface StatsCardProps {
  title: string;
  value: string | number;
  icon: JSX.Element;
  className?: string;
  gradientFrom?: string;
  gradientTo?: string;
  textColor?: string;
}

// Memoize the StatsCard for better performance
const StatsCard = memo(
  ({
    title,
    value,
    icon,
    className,
    gradientFrom,
    gradientTo,
    textColor,
  }: StatsCardProps) => (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-md p-3.5 shadow-sm transition-all duration-200 ease-in-out',
        `bg-gradient-to-br ${gradientFrom || 'from-indigo-50'} ${gradientTo || 'to-indigo-100/80'}`,
        'border hover:translate-y-[-2px] hover:shadow-md',
        className,
      )}
    >
      <div
        className={cn(
          'mb-1 text-xs font-semibold uppercase tracking-wide',
          textColor || 'text-blue-700',
        )}
      >
        {title}
      </div>
      <div className="flex items-center gap-2.5">
        <div className={cn('text-2xl font-bold', textColor || 'text-blue-700')}>
          {value}
        </div>
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/70 shadow-sm">
          {icon}
        </div>
      </div>
    </div>
  ),
);

StatsCard.displayName = 'StatsCard';

// Professional loading component with advanced effects
const LoadingIndicator = memo(() => (
  <div className="flex h-[60vh] min-h-[16rem] w-full flex-col items-center justify-center px-4 py-10">
    <div className="relative mb-2">
      {/* Background glow effect */}
      <div className="absolute -inset-4 animate-pulse rounded-full bg-indigo-300/10 blur-xl"></div>

      {/* Outer spinning ring */}
      <div className="absolute -inset-1 rounded-full border-2 border-indigo-200/50 dark:border-indigo-800/50"></div>
      <div
        className="absolute -inset-1 animate-spin rounded-full border-2 border-transparent border-t-indigo-500"
        style={{ animationDuration: '3s' }}
      ></div>

      {/* Middle spinning ring */}
      <div
        className="absolute -inset-0.5 animate-spin rounded-full border-2 border-transparent border-b-blue-500"
        style={{ animationDuration: '2s', animationDirection: 'reverse' }}
      ></div>

      {/* Inner container with gradient */}
      <div className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-white to-indigo-50 shadow-lg dark:from-indigo-950 dark:to-indigo-900/80">
        {/* Centered spinner */}
        <Loader2 className="h-11 w-11 animate-spin text-indigo-600 drop-shadow-sm dark:text-indigo-400" />
      </div>

      {/* Decorative dots */}
      <div className="absolute -right-1 -top-1 h-3 w-3 animate-ping rounded-full bg-blue-500 opacity-75"></div>
      <div
        className="absolute -bottom-1 -left-1 h-3 w-3 animate-ping rounded-full bg-indigo-600 opacity-75"
        style={{ animationDelay: '0.5s' }}
      ></div>
    </div>

    {/* Text with pulsing dots */}
    <div className="mt-6 flex flex-col items-center">
      <span className="mb-1.5 text-lg font-medium tracking-wide text-gray-700 dark:text-gray-200">
        جاري تحميل العروض
      </span>
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-600 dark:bg-indigo-400"
          style={{ animationDelay: '0s' }}
        ></span>
        <span
          className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500 dark:bg-indigo-500"
          style={{ animationDelay: '0.2s' }}
        ></span>
        <span
          className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 dark:bg-indigo-600"
          style={{ animationDelay: '0.4s' }}
        ></span>
      </div>
    </div>
  </div>
));

LoadingIndicator.displayName = 'LoadingIndicator';

// Add a memoized compact item component to improve performance
const HealthyItemRow = memo(
  ({
    item,
    isSelected,
    onClick,
  }: {
    item: { id: number; name: string; price: number };
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center justify-between border-b border-gray-100 px-1 py-1 text-xs last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50',
        isSelected &&
          'bg-blue-50 hover:bg-blue-50/80 dark:bg-blue-900/20 dark:hover:bg-blue-900/30',
      )}
      onClick={onClick}
      aria-pressed={isSelected}
    >
      <div className="flex items-center gap-1 truncate pr-1">
        <span
          className={cn(
            'inline-flex h-4 min-w-5 items-center justify-center rounded px-1 text-[10px] font-medium',
            isSelected
              ? 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
              : 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
          )}
        >
          {item.id}
        </span>
        <span
          className={cn(
            'truncate font-medium',
            isSelected
              ? 'text-blue-800 dark:text-blue-300'
              : 'text-gray-700 dark:text-gray-300',
          )}
          title={item.name}
        >
          {item.name}
          {isSelected && (
            <CheckCircle className="ml-1 inline-block h-3 w-3 text-blue-600 dark:text-blue-400" />
          )}
        </span>
      </div>
      <span
        className={cn(
          'shrink-0 rounded px-1 py-0.5 text-[10px] font-medium',
          isSelected
            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
        )}
      >
        {item.price}
      </span>
    </button>
  ),
);

HealthyItemRow.displayName = 'HealthyItemRow';

const CompetitorsDialogComponent = ({ closeFn, isOpen, item }: Props) => {
  // Extract the HealthyItemID from the item and ensure it's a number
  const healthyItemID = item?.HealthyItemID ?? 0;
  const hasValidItemID = healthyItemID > 0;

  // Add state for selecting an item to filter
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  // Optimize query with appropriate options for performance
  const {
    data: itemOffers,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['itemOffers', healthyItemID],
    queryFn: async (): Promise<ItemOfferToday[]> => {
      try {
        const data = await GetItemOffersToday(healthyItemID);
        return data || []; // Return empty array if no data to prevent null issues
      } catch (error) {
        console.error('Error fetching offers:', error);
        throw error;
      }
    },
    enabled: isOpen && hasValidItemID, // Only fetch when dialog is open and we have a valid item ID
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (replaces cacheTime)
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once on failure
    placeholderData: [], // Use empty array as placeholder (replaces keepPreviousData)
  });

  // Handle manual refresh - memoized to prevent recreating the function
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Calculate statistics for the cards - memoized to prevent recalculating on every render
  const stats = useMemo(() => {
    if (!itemOffers || itemOffers.length === 0)
      return {
        maxDiscount: 0,
        minDiscount: 0,
        suppliersCount: 0,
        totalOffers: 0,
      };

    // Use the filtered offers if an item is selected
    const offersToCalculate = selectedItemId
      ? itemOffers.filter((offer) => offer.healthyItemID === selectedItemId)
      : itemOffers;

    // Use a single pass over the data to calculate all stats at once
    let maxDiscount = -Infinity;
    let minDiscount = Infinity;
    const uniqueSuppliers = new Set();

    offersToCalculate.forEach((offer: ItemOfferToday) => {
      // Update max and min in a single pass
      if (offer.discount > maxDiscount) maxDiscount = offer.discount;
      if (offer.discount < minDiscount) minDiscount = offer.discount;
      uniqueSuppliers.add(offer.clientID);
    });

    return {
      maxDiscount: maxDiscount !== -Infinity ? maxDiscount : 0,
      minDiscount: minDiscount !== Infinity ? minDiscount : 0,
      suppliersCount: uniqueSuppliers.size,
      totalOffers: offersToCalculate.length,
    };
  }, [itemOffers, selectedItemId]);

  // Memoize derived state
  const hasData = useMemo(
    () => itemOffers && itemOffers.length > 0,
    [itemOffers],
  );
  const showStats = useMemo(
    () => !isLoading && !isFetching && hasData,
    [isLoading, isFetching, hasData],
  );

  // Memoize dialog close handler
  const handleDialogChange = useCallback(
    (open: boolean) => {
      if (!open) closeFn();
    },
    [closeFn],
  );

  // Extract healthy items data
  const healthyItems = useMemo(() => {
    if (!itemOffers || itemOffers.length === 0) return [];

    // Use a Map to collect unique items by ID
    const itemsMap = new Map();

    itemOffers.forEach((offer) => {
      if (offer.healthyItemID && !itemsMap.has(offer.healthyItemID)) {
        itemsMap.set(offer.healthyItemID, {
          id: offer.healthyItemID,
          name: offer.healthyItemName || 'غير معروف',
          price: offer.healthyItemPrice || 0,
        });
      }
    });

    return Array.from(itemsMap.values());
  }, [itemOffers]);

  // Filter offers based on selected item
  const filteredOffers = useMemo(() => {
    if (!itemOffers || !selectedItemId) return itemOffers || [];

    return itemOffers.filter((offer) => offer.healthyItemID === selectedItemId);
  }, [itemOffers, selectedItemId]);

  // Memoize the stats cards to prevent recreating on every render
  const statsCards = useMemo(() => {
    if (!showStats) return null;

    // Function to handle clicking on an item row
    const handleItemClick = (id: number) => {
      setSelectedItemId((prevId) => (prevId === id ? null : id));
    };

    // Get selected item name for display
    const selectedItem = selectedItemId
      ? healthyItems.find((item) => item.id === selectedItemId)
      : null;

    return (
      <div className="flex w-72 flex-col gap-3 border-r border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900/80">
        <h3 className="mb-1 flex items-center justify-between text-sm font-semibold text-gray-600 dark:text-gray-400">
          <span>إحصائيات العروض</span>
          {selectedItem && (
            <Badge
              variant="outline"
              className="bg-blue-50 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {selectedItem.name}
            </Badge>
          )}
        </h3>
        <StatsCard
          title="أقصى خصم"
          value={`${stats.maxDiscount}%`}
          icon={<TrendingUp className="h-4.5 w-4.5 text-green-600" />}
          gradientFrom="from-green-50"
          gradientTo="to-green-100/80"
          textColor="text-green-700 dark:text-green-400"
          className="border-green-200 dark:border-green-800/50 dark:from-green-950/70 dark:to-green-900/50"
        />
        <StatsCard
          title="أدنى خصم"
          value={`${stats.minDiscount}%`}
          icon={<TrendingDown className="h-4.5 w-4.5 text-red-600" />}
          gradientFrom="from-red-50"
          gradientTo="to-red-100/80"
          textColor="text-red-700 dark:text-red-400"
          className="border-red-200 dark:border-red-800/50 dark:from-red-950/70 dark:to-red-900/50"
        />
        <StatsCard
          title="عدد الموردين"
          value={stats.suppliersCount}
          icon={<Users className="h-4.5 w-4.5 text-blue-600" />}
          gradientFrom="from-blue-50"
          gradientTo="to-blue-100/80"
          textColor="text-blue-700 dark:text-blue-400"
          className="border-blue-200 dark:border-blue-800/50 dark:from-blue-950/70 dark:to-blue-900/50"
        />
        <StatsCard
          title="إجمالي العروض"
          value={stats.totalOffers}
          icon={<ClipboardList className="h-4.5 w-4.5 text-purple-600" />}
          gradientFrom="from-purple-50"
          gradientTo="to-purple-100/80"
          textColor="text-purple-700 dark:text-purple-400"
          className="border-purple-200 dark:border-purple-800/50 dark:from-purple-950/70 dark:to-purple-900/50"
        />

        {/* List of Healthy Items - Clickable for filtering */}
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-400">
              الأصناف ({healthyItems.length})
            </h3>
            {selectedItemId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItemId(null)}
                className="h-6 px-2 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                عرض الكل
              </Button>
            )}
          </div>

          <div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 h-[220px] overflow-auto rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
            {healthyItems.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {healthyItems.map((item) => (
                  <HealthyItemRow
                    key={item.id}
                    item={item}
                    isSelected={selectedItemId === item.id}
                    onClick={() => handleItemClick(item.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                لا توجد أصناف
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [showStats, stats, healthyItems, selectedItemId]);

  // Conditionally render main content to reduce complexity
  const renderMainContent = useCallback(() => {
    if (isLoading || isFetching) {
      return <LoadingIndicator />;
    }

    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
          <div className="relative mb-5">
            {/* Subtle background glow */}
            <div className="absolute -inset-4 rounded-full bg-red-100/50 blur-xl dark:bg-red-900/10"></div>

            {/* Icon container with gradient */}
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-b from-red-50 to-red-100 shadow-lg dark:from-red-900/60 dark:to-red-950/60">
              <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />

              {/* Animated pulse effect */}
              <div
                className="absolute -inset-1 animate-pulse rounded-full border border-red-200 dark:border-red-800/50"
                style={{ animationDuration: '2.5s' }}
              ></div>
            </div>
          </div>

          <p className="mb-6 text-xl font-medium text-red-600 dark:text-red-400">
            حدث خطأ أثناء تحميل البيانات
          </p>

          <div className="flex gap-4">
            <Button
              variant="outline"
              className="border-red-200 text-red-600 transition-all duration-300 hover:border-red-300 hover:bg-red-50 dark:border-red-800/50 dark:text-red-400 dark:hover:bg-red-900/20"
              onClick={closeFn}
            >
              <span>إغلاق</span>
            </Button>
            <Button
              variant="default"
              className="bg-blue-600 text-white shadow-md transition-all duration-300 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
              onClick={handleRefresh}
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              <span>إعادة المحاولة</span>
            </Button>
          </div>
        </div>
      );
    }

    if (!hasValidItemID) {
      return (
        <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
          <div className="relative mb-5">
            {/* Subtle background glow */}
            <div className="absolute -inset-4 rounded-full bg-amber-100/50 blur-xl dark:bg-amber-900/10"></div>

            {/* Icon container with gradient */}
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-b from-amber-50 to-amber-100 shadow-lg dark:from-amber-900/60 dark:to-amber-950/60">
              <Info className="h-12 w-12 text-amber-600 dark:text-amber-400" />

              {/* Animated pulse effect */}
              <div
                className="absolute -inset-1 animate-pulse rounded-full border border-amber-200 dark:border-amber-800/50"
                style={{ animationDuration: '2.5s' }}
              ></div>
            </div>
          </div>

          <p className="mb-6 text-xl font-medium text-amber-600 dark:text-amber-400">
            لا يمكن عرض العروض - الصنف غير مرتبط بهلثي
          </p>

          <Button
            variant="outline"
            className="group border-amber-200 px-6 text-amber-600 transition-all duration-300 hover:border-amber-300 hover:bg-amber-50 dark:border-amber-800/50 dark:text-amber-400 dark:hover:bg-amber-900/20"
            onClick={closeFn}
          >
            <span>إغلاق</span>
            <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              <X className="h-4 w-4" />
            </span>
          </Button>
        </div>
      );
    }

    if (hasData) {
      return (
        <div className="flex h-full overflow-hidden rounded-b-lg bg-white dark:bg-gray-950">
          {showStats && statsCards}
          <div className="flex-1 overflow-auto">
            <Suspense fallback={<LoadingIndicator />}>
              <CompetitorsDiscountTable data={filteredOffers || []} />
            </Suspense>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
        <div className="relative mb-4">
          {/* Background glow */}
          <div className="absolute -inset-3 rounded-full bg-gray-100 blur-xl dark:bg-gray-800/30"></div>

          {/* Icon container with gradient */}
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-b from-gray-50 to-gray-100 shadow-lg dark:from-gray-800 dark:to-gray-900">
            <ShieldCheck className="h-12 w-12 text-gray-500 dark:text-gray-400" />

            {/* Subtle pulse ring */}
            <div
              className="absolute -inset-1 animate-pulse rounded-full border border-gray-200 dark:border-gray-700"
              style={{ animationDuration: '3s' }}
            ></div>
          </div>
        </div>

        <p className="mb-5 text-xl font-medium text-gray-600 dark:text-gray-400">
          لا توجد عروض متاحة لهذا الصنف
        </p>

        <Button
          variant="outline"
          className="group border-gray-300 px-6 text-gray-600 transition-all duration-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
          onClick={closeFn}
        >
          <span>إغلاق</span>
          <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-0.5">
            <X className="h-4 w-4" />
          </span>
        </Button>
      </div>
    );
  }, [
    isLoading,
    isFetching,
    isError,
    hasValidItemID,
    hasData,
    filteredOffers,
    closeFn,
    handleRefresh,
    showStats,
    statsCards,
  ]);

  // Use isOpen directly to avoid additional re-renders
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        size="5xl"
        hiddenCloseIcon
        className="p-0"
        overlayClass="backdrop-blur-xs"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50 p-3 dark:from-indigo-950/80 dark:to-blue-950/80">
          <div className="flex items-center gap-3.5">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-indigo-100 p-2 shadow-xl shadow-indigo-300/20 dark:bg-indigo-900/60 dark:shadow-indigo-900/30">
              <Percent className="h-9 w-9 text-indigo-600 dark:text-indigo-300" />
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-semibold text-indigo-700 dark:text-indigo-300">
                خصومات السوق
              </span>
              <div className="mt-0.5 flex items-center text-sm text-gray-600 dark:text-gray-400">
                <span className="max-w-[240px] truncate">
                  {item?.ClientItemName}
                </span>
                <Badge className="mr-2 rounded-md border-blue-200 bg-blue-100 px-2 py-0.5 text-blue-800 dark:border-blue-800 dark:bg-blue-900/80 dark:text-blue-200">
                  {formatNumberWithCommas(item?.Discount, 2)} %
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {isError && !isFetching && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                className="h-10 w-10 rounded-full border-blue-200 hover:bg-blue-100 hover:text-blue-700 dark:border-blue-800 dark:hover:bg-blue-900/40 dark:hover:text-blue-300"
                title="تحديث البيانات"
              >
                <RefreshCcw className="h-4.5 w-4.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={closeFn}
              className="h-10 w-10 rounded-full hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/40 dark:hover:text-red-300"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogTitle>

        {/* Hidden DialogDescription to fix accessibility warning */}
        <DialogDescription className="hidden">
          عرض خصومات السوق للصنف {item?.ClientItemName}
        </DialogDescription>

        {/* Main Content - conditionally rendered via memoized function */}
        {renderMainContent()}
      </DialogContent>
    </Dialog>
  );
};

// Use memo for the entire component to prevent unnecessary re-renders
const CompetitorsDialog = memo(CompetitorsDialogComponent);

export default CompetitorsDialog;
